import omit from 'lodash/omit'
import type { CartItem, GiftcardCartItem } from '~/composables/types/cart'

export const useCartValidation = () => {
  const { cart } = useCartWithSubdomainLogic()
  const interval = ref()
  const { requireAgeOfMajority, handleValidationErrors } =
    useCartErrorHandlers()
  const { initializePricings } = useInitializePricingsForLegacyCart(cart)

  const getCartItemForValidation = (item: CartItem) => {
    const isGiftCard = item.type === 'GIFT_CARD'

    if (isGiftCard) {
      const giftcard = item as GiftcardCartItem
      return {
        type: giftcard.type,
        quantity: giftcard.quantity,
        value: parseInt(giftcard?.pricings?.price?.total_price?.amount || '0'),
        ship: giftcard.giftcardAddon !== 'PDF',
        ...(giftcard.giftcardAddon !== 'PDF'
          ? { ship_type: giftcard.giftcardAddon }
          : {}),
        ...(giftcard.eventDescriptionId
          ? { event: giftcard.eventDescriptionId }
          : {}),
      }
    }

    return {
      ...omit(item, 'supplierId'),
      product: item.id,
      price: parseInt(item.pricings?.price?.total_price?.amount || '0'),
    }
  }

  const performValidation = async () => {
    if (cart.value.items && cart.value.items.length === 0) return
    await initializePricings()

    await kftApiRequest('/v1/checkout/cart/validate', {
      method: 'POST',
      body: {
        items: cart.value.items.map(getCartItemForValidation),
      },
    }).catch(async (e) => {
      await handleValidationErrors(e)
    })
  }

  const setupValidationPolling = async () => {
    await performValidation()
    const oneMinute = 60000
    interval.value = setInterval(performValidation, oneMinute * 10)
  }

  const deactivateValidationPolling = async () => {
    await performValidation()
    clearInterval(interval.value)
  }

  return {
    requireAgeOfMajority,
    setupValidationPolling,
    performValidation,
    deactivateValidationPolling,
  }
}
